/**
 * Fila persistente de créditos de pontos que falharam quando deveriam
 * ser aplicados ao profile. Resolve o problema do DailyBonus reveal:
 *
 *   1. User toca "Abrir presente" → ganha +10 coins (UI confirma)
 *   2. Em paralelo, app tenta `updateUserProfile({ points: +10 })`
 *   3. Se rede ruim / Supabase down nesse instante exato → falha
 *   4. UI já mostrou "+10 Six Coins!" — user vai cobrar
 *
 * Antes deste módulo: silenciosamente perdia. Agora: persiste em
 * localStorage e tenta de novo no próximo mount + no `online` event.
 *
 * Aplicação: DailyBonusModal (queue no catch), App.tsx ou qualquer
 * lugar que monte com profile carregado (flush). Pode escalar pra
 * outros fluxos (link tracking, missões) reusando.
 */
import { safeGetJSON, safeSetJSON, safeRemove } from './safeStorage';

const KEY = '@sixpass:pending-credits-v1';

export type PendingCreditSource =
  | 'daily-bonus'
  | 'link-click'
  | 'mission'
  | 'manual';

export interface PendingCredit {
  /** Unique id pra dedupe se algum dia formos longe num storage compartilhado. */
  id: string;
  /** Quantos pontos creditar. */
  amount: number;
  /** Quando foi enfileirado (epoch ms). */
  queuedAt: number;
  /** De onde veio o crédito (analytics + debug). */
  source: PendingCreditSource;
}

/** Quantos itens manter na fila no máximo. Defesa contra storage cheio. */
const MAX_QUEUE = 20;

/** TTL — itens muito antigos (>7d) são descartados no próximo flush.
 *  Evita "fantasma" de créditos antigos rebobinando pro user depois. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function readQueue(): PendingCredit[] {
  const parsed = safeGetJSON<unknown>(KEY, []);
  if (!Array.isArray(parsed)) return [];
  const now = Date.now();
  return parsed.filter((p): p is PendingCredit => {
    if (typeof p !== 'object' || p === null) return false;
    const pc = p as Partial<PendingCredit>;
    if (typeof pc.amount !== 'number' || pc.amount <= 0) return false;
    if (typeof pc.queuedAt !== 'number') return false;
    // Expira itens > 7d
    if (now - pc.queuedAt > MAX_AGE_MS) return false;
    return true;
  });
}

function writeQueue(items: PendingCredit[]): void {
  if (items.length === 0) {
    safeRemove(KEY);
    return;
  }
  // Mantém só os mais recentes se passar do máximo
  const capped = items.length > MAX_QUEUE ? items.slice(-MAX_QUEUE) : items;
  safeSetJSON(KEY, capped);
}

/**
 * Enfileira um crédito pra retry. Chamar no catch quando
 * updateUserProfile falha.
 */
export function queuePendingCredit(
  amount: number,
  source: PendingCreditSource,
): void {
  if (amount <= 0) return;
  const list = readQueue();
  list.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    amount,
    queuedAt: Date.now(),
    source,
  });
  writeQueue(list);
}

/**
 * Processa a fila. Pra cada item, chama `applyDelta(amount)`. Items
 * que sucedem são removidos; que falham permanecem pra próxima
 * tentativa.
 *
 * Idempotente: pode chamar várias vezes sem efeito colateral.
 *
 * @returns `{ processed, failed }` — contagem útil pra log/UI.
 */
export async function flushPendingCredits(
  applyDelta: (delta: number, source: PendingCreditSource) => Promise<void>,
): Promise<{ processed: number; failed: number; totalAmount: number }> {
  const list = readQueue();
  if (list.length === 0) {
    return { processed: 0, failed: 0, totalAmount: 0 };
  }

  let processed = 0;
  let totalAmount = 0;
  const remaining: PendingCredit[] = [];

  for (const credit of list) {
    try {
      await applyDelta(credit.amount, credit.source);
      processed += 1;
      totalAmount += credit.amount;
    } catch {
      // Mantém pra próxima tentativa — não vamos perder o crédito.
      remaining.push(credit);
    }
  }

  writeQueue(remaining);
  return { processed, failed: remaining.length, totalAmount };
}

/** Helper pra ler tamanho da fila sem processar (UI/debug). */
export function pendingCreditsCount(): number {
  return readQueue().length;
}
